// require('dotenv').config()
import React from 'react'
import Refund from "../Refund";
import "./settings.css"
import FieldHistoryDiff from './FieldHistoryDiff';
import { FaCog } from "react-icons/fa";
import { useState, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../context/AuthProvider';
import { useCustomer } from '../../context/CustomerProvider';
import { useHistory, useLocation } from "react-router-dom"
import { Button, Alert, Switch, Space, Table, Tag, Modal, Input, Tooltip, Dropdown, Menu, notification, message, Tabs, Form, Divider, Drawer, Collapse, Spin, Popconfirm } from 'antd';
import BrandSelectionCard from '../Menu/BrandSelectionCard';
import ActiveBrandBlock from '../Menu/ActiveBrandBlock';
import { normalizeBrandIds } from '../../lib/normalizeBrandIds';
import { GiConfirmed } from 'react-icons/gi';
import { GrCircleAlert } from 'react-icons/gr';
import { CaretDownOutlined, CaretRightOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, CopyOutlined } from '@ant-design/icons';


const { Column, ColumnGroup } = Table;
const { TextArea } = Input;

const AUDITED_REVIEW_FIELDS = ['policy', 'faqs', 'tone'];

function isValidReviewField(field) {
  return AUDITED_REVIEW_FIELDS.includes(field);
}

function getFieldParam(search) {
  const field = new URLSearchParams(search).get('field');
  return isValidReviewField(field) ? field : null;
}

/** Shorten Firestore-style ids for secondary line in brand dropdown */
function shortBrandId(id) {
  if (!id || typeof id !== 'string') return '';
  return id.length > 20 ? `${id.slice(0, 10)}…${id.slice(-4)}` : id;
}

/** Brands picker for add/edit client modal: search + scrollable table (no chip overflow). */
function ClientModalBrandTable({
  value,
  onChange,
  brands,
  search,
  onSearchChange,
}) {
  const filtered = useMemo(() => {
    const list = brands || [];
    const q = (search || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (b) =>
        b.name?.toLowerCase().includes(q)
        || String(b.id).toLowerCase().includes(q),
    );
  }, [brands, search]);

  const allIds = useMemo(() => (brands || []).map((b) => b.id), [brands]);

  const selectedRowKeys = useMemo(() => {
    const v = value || [];
    if (v.includes('all')) return allIds;
    return v;
  }, [value, allIds]);

  const handleRowSelectionChange = (keys) => {
    if (!keys.length) {
      onChange?.([]);
      return;
    }
    if (
      allIds.length > 0
      && keys.length === allIds.length
      && allIds.every((id) => keys.includes(id))
    ) {
      onChange?.(['all']);
      return;
    }
    onChange?.(keys);
  };

  const dataSource = useMemo(
    () =>
      filtered.map((b) => ({
        ...b,
        key: b.id,
      })),
    [filtered],
  );

  const columns = [
    {
      title: 'Brand',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Brand ID',
      dataIndex: 'id',
      key: 'id',
      width: 220,
      render: (id) => (
        <code className="client-modal-brand-table__id">{shortBrandId(id)}</code>
      ),
    },
  ];

  return (
    <div className="client-modal-brand-table-wrap">
      <Input
        allowClear
        className="client-modal-brand-table__search"
        placeholder="Search brands by name or ID…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        prefix={<SearchOutlined />}
      />
      <Table
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ y: 380 }}
        className="client-modal-brand-table"
        rowSelection={{
          selectedRowKeys,
          onChange: handleRowSelectionChange,
          preserveSelectedRowKeys: true,
          hideSelectAll: true,
          columnWidth: 48,
        }}
        locale={{
          emptyText: search ? 'No brands match your search.' : 'No brands available.',
        }}
      />
    </div>
  );
}

export default function Settings() {

  const history = useHistory();
  const location = useLocation();

  const { currentUser, brandId, brands, triggerBrand } = useAuth()
  const [activeTab, setActiveTab] = useState(() => getFieldParam(location.search) ? 'policy' : 'brand');
  const [brandSettingsHydratedFor, setBrandSettingsHydratedFor] = useState(null);
  const latestBrandIdRef = useRef(brandId);
  const handledFieldFocusRef = useRef(null);

  const { addAgent, deleteAgent, getAgents } = useCustomer()
  const { addTeam, deleteTeam, getTeam } = useCustomer()
  const { team, setTeam } = useCustomer()
  const { selectorTeam, setSelectorTeam, getTeam2 } = useCustomer()
  const { setAutomations, setFAQ, getAutomations, brandValues, stripeUsage, sendInvite, setBrandValues, stripeAccount, getStripeLink, stripeSubscribe, setNylas, addIntegration, getIntegrations, saveIntegration, deleteIntegration, fieldHistory, checkOffField, refreshPendingReviews } = useCustomer()
  const { getKeys, sendApiKeys, gorgiasKeys, getGorgiasKeys, getApi, resetApi, trackingKey, getTrackingKey, createApi, addClient, getClients, deleteClient, setZendeskOAuth, getZendeskOAuth } = useCustomer();

  // Zendesk OAuth (client credentials). Secrets are write-only from here: the
  // backend returns "configured" booleans, never a decrypted value, so the
  // inputs stay blank and a blank input on save keeps the stored secret.
  const [zendeskOAuth, setZendeskOAuthState] = useState(null);
  const [zendeskSubdomain, setZendeskSubdomain] = useState('');
  const [zendeskClientId, setZendeskClientId] = useState('');
  const [zendeskClientSecret, setZendeskClientSecret] = useState('');
  const [zendeskWebhookSecret, setZendeskWebhookSecret] = useState('');
  const [zendeskSaving, setZendeskSaving] = useState(false);
  const [zendeskMessage, setZendeskMessage] = useState('');
  // Shown once, right after it is generated. It is stored encrypted and cannot
  // be read back, so it has to be copied into Zendesk now.
  const [zendeskNewSecret, setZendeskNewSecret] = useState('');

  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);

  // const [name, setName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [inviteLink, setInviteLink] = useState('https://calendly.com/chirpish/demo');
  const [expiration, setExpiration] = useState('');

  const [aiEnabled, setAiEnabled] = useState(false);
  const [automationsEnabled, setAutomationsEnabled] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showCodeGorgias, setShowCodeGorgias] = useState(false);
  const [showCodeChirpish, setShowCodeChirpish] = useState(false);

  const [lock, setLock] = useState(false);
  const [aiLock, setAiLock] = useState(false);

  useEffect(() => {
    console.log(`Current brand: ${brandId}`);
    if (brandId === null) {
      setLock(true);
      setAiLock(true);
    } else {
      setLock(false);
      setAiLock(false);
    }
  }, [brandId]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = await getApi();
        setChirpishApi(api);
      } catch (error) {
        console.log("Error setting API key.");
      }
    };

    fetchData();
  }, [currentUser, brandId]);


  const lockSettings = {
    pointerEvents: lock ? 'none' : 'auto',
    opacity: lock ? '50%' : '100%'
  }

  const aiLockSettings = {
    pointerEvents: aiLock ? 'none' : 'auto',
    opacity: aiLock ? '50%' : '100%'
  }

  // GET THE NYLAS CODE AND USE IT TO SAVE THE NYLAS DATA TO THE DATABASE FOR THE USER
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nylasValue = params.get('nylas');
    if (nylasValue) {
      callSetNylas(nylasValue);
    }
  }, []);

  async function callSetNylas(code) {
    // Your async function logic here
    console.log("Retrieved Nylas code, setting it now..")
    try {
      const set = await setNylas(code);
      console.log("Successfully set Nylas!")
    } catch (error) {
      console.log("Error setting Nylas..")
    }
  }

  // const redirectToO = () => {
  //   history.push('/o');
  // };

  const onChange = (identifier, checked) => {
    console.log(`Switching ${identifier} to ${checked}`);
    set(identifier, checked)
  };

  async function set(identifier, value) {
    const callSetIdentifier = await setAutomations(identifier, value)

    // Update the local state with the new value
    setAutomations2((prevAutomations) => ({
      ...prevAutomations,
      [identifier]: value,
    }));
  }

  const handleEmailChange = (e) => {
    const inputEmail = e.target.value;
    // You can use a regular expression to validate the email format
    const isValid = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(inputEmail);
    setEmail(inputEmail);
    setIsEmailValid(isValid);
  };

  // useEffect(() => {
  //   console.log("getting team for selector on load")
  //   callTeam2();
  // }, [brandId]);

  // async function callTeam2() {
  //   try {
  //     const res = await getTeam2();
  //     console.log("ressdsdsds: ", res)
  //   } catch (error) {
  //     console.log("fell to an error?")
  //     console.log(error)
  //   }
  //   console.log("how could I not fall into here?")
  // }

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const [isModalOpenNew, setIsModalOpenNew] = useState(false);
  const [isModalOpen2New, setIsModalOpen2New] = useState(false);
  const [isCheckedNew, setIsCheckedNew] = useState(false);

  const [stripeMessage, setStripeMessage] = useState('Manage your stripe account..');
  const [stripeType, setStripeType] = useState('plan');
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [gorgiasAdminEmail, setGorgiasAdminEmail] = useState("");
  const [gorgiasDomain, setGorgiasDomain] = useState("");
  const [gorgiasApiKey, setGorgiasApiKey] = useState("");
  const [authorizationKey, setAuthorizationKey] = useState(null);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const toggleModal = () => {
    console.log("in here too")
    setIsModalVisible(!isModalVisible);
    setAiEnabled(false)
  };

  // const handleCreate = async () => {
  //   try {
  //     if (stripeType === 'billing') {
  //       // Redirect to google.com in a new window
  //       window.open('https://billing.stripe.com/p/login/3csaIj88Dcrn8j63cc', '_blank');
  //     } else {
  //       // Your existing subscription creation logic here

  //       // Retrieve the referral information from the global 'window' object
  //       const aff_data = window.LinkMink('pub_live_2foBDbORbBKHAbQqSJC4').getReferralData();

  //       // Set lm_data to null if it's undefined or null
  //       const lm_data = aff_data || null;

  //       const stripe = await stripeCreate(brandName, email, lm_data);
  //       console.log("Stripe: ", stripe)

  //       const subscribe = await stripeSubscribe(email);
  //       console.log("Subscribe: ", subscribe)

  //       console.log("Successfully created the user a Stripe account")

  //       // Successful
  //       setIsModalVisible(!isModalVisible);
  //       setAiEnabled(true); // Update the state immediately
  //       await setBrandValues([{ name: "ai", value: true }]);
  //     }
  //     console.log(lm_data);
  //   } catch (error) {
  //     // Handle unexpected errors
  //     console.error('Error creating subscription:', error);
  //     // You can display an error message to the user if needed
  //     setIsModalVisible(!isModalVisible);
  //     setAiEnabled(false);
  //   }
  // };

  const generateSubscriptionLink = async () => {
    try {
      const aff_data = window.LinkMink('pub_live_2foBDbORbBKHAbQqSJC4').getReferralData();
      const lm_data = aff_data || null;
      console.log("lm_data: ", lm_data);
      const stripe = await getStripeLink(brandId, lm_data)
      console.log(stripe);
      setStripeLink(stripe);
    } catch (error) {
      console.error(error);
    }
  }

  const redirectToStripe = async () => {
    window.open(stripeLink, '_blank');
    setAiEnabled(false);
    setIsModalVisible(false);
  };


  const getExpiration = () => {
    const expirationTime = new Date();
    // expiration is 24 hours from now
    expirationTime.setUTCHours(expirationTime.getUTCHours() + 24);
    // expirationTime.setUTCHours(expirationTime.getUTCHours());

    // convert to UTC timestamp into Unix value
    const timestamp = Math.floor(expirationTime.getTime() / 1000);
    return timestamp
  }

  const handleOk = async () => {
    try {
      const send = await sendInvite(brandId, emailAddress)
      console.log(send)
      setEmailAddress('')
      setIsModalOpen(false)
      openPopup('Invite Sent!.', `You have sent an invite link to ${emailAddress}`, <GiConfirmed />, '#D1FFBD')
    } catch (error) {
      console.error(error)
    }
  };


  const showModal2 = () => {
    setIsModalOpen2(true);
  };

  const setMaster = () => {
    localStorage.removeItem('user');
    setUser("Master")
  }

  const handleOk2 = async () => {
    setIsModalOpen2(false);
  };

  const handleCancel2 = () => {
    setIsModalOpen2(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    // setName("")
  };

  const onChange2 = (value) => {
    console.log(`selected ${value}`);
    // SET TO SESSION VARIABLE
    localStorage.setItem('user', value);
    setUser(value)
  };

  const onSearch2 = (value) => {
    console.log('search:', value);
  };

  async function handleDelete(id) {
    console.log("clicked handleDelete with id: ", id)
    const res = await deleteTeam(id);
    callTeam();
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Role',
      key: 'tags',
      dataIndex: 'tags',
      render: (_, { tags }) => (
        <>
          {tags.map((tag) => {
            let color = tag.length > 5 ? 'geekblue' : 'green';
            if (tag === 'loser') {
              color = 'volcano';
            }
            return (
              <Tag color={color} key={tag}>
                {tag.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Manage',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {/* <a>Invite {record.name}</a> */}
          <a onClick={() => handleDelete(record.firebaseId)}>Delete</a>
        </Space>
      ),
    },
  ];


  async function callTeam() {
    try {
      const res = await getTeam();
      console.log("res: ", res)
    } catch (error) {
      console.log(error)
    }
  }


  const fetchIntegrations = async () => {
    try {
      // Call the asynchronous function
      const fetchedIntegrations = await getIntegrations();
      // console.log("Response: ", fetchedIntegrations)
      // console.log("argssss: ", args)

      // Combine the presetArgs with the fetchedIntegrations
      const updatedArgs = [...presetArgs, ...fetchedIntegrations];

      // Update the state with the combined array
      setArgs(updatedArgs);
    } catch (error) {
      console.error("Error fetching integrations:", error);
    }
  };


  useEffect(() => {
    // Call the asynchronous function
    fetchIntegrations();
  }, [brandId]);

  const [user, setUser] = useState("Master");

  const presetArgs = [
    {
      "id": "automations",
      "title": "Automations",
      "description": "This is for automating all the requests that don't take actions on behalf of your store. This can include edgecases, but these can be mimimized using policy.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": [] // {"arg": "dgdhrrdhdr", "value": "hftbfdbdfbfb"}
    },
    {
      "id": "refund",
      "title": "Refunds",
      "description": "Let Shopify refund order when it matches policy.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": [] // {"arg": "dgdhrrdhdr", "value": "hftbfdbdfbfb"}
    },
    {
      "id": "cancellation",
      "title": "Cancellation",
      "description": "Let Shopify cancel order when it matches policy.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": []
    },
    {
      "id": "tracking",
      "title": "Tracking",
      "description": "Automate inquiries that retrieve tracking information.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": []
    },
    {
      "id": "faq",
      "title": "FAQs",
      "description": "Automate FAQ inquiries based on FAQ box below.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": []
    },
    {
      "id": "modify",
      "title": "Modify",
      "description": "Let Shopify modify order name, email, and shipping address.",
      "builtIn": true,
      "automation": false,
      "webhook": "https://google.com",
      "arguments": []
    },
  ]
  const [args, setArgs] = useState(presetArgs);

  const [brand, setBrand] = useState("");
  const [policy, setPolicy] = useState("");
  const [faqNew, setFaqNew] = useState("");
  const [tone, setTone] = useState("");
  const [testMode, setTestMode] = useState(true);



  // Persists via setFAQ (legacy API name—see CustomerProvider) per box identifier.
  const handleSaveButtonClick = async (boxName) => {
    switch (boxName) {
      case 'policy':
        console.log("Saving policy: ", policy)
        await setFAQ("policy", policy)
        openPopup('Policy Settings Saved.', 'Policy has been updated!', <GiConfirmed />, '#D1FFBD')
        break;
      case 'faqs':
        console.log("Saving new faq: ", faqNew)
        await setFAQ("faqs", faqNew)
        openPopup('FAQs Settings Saved.', 'FAQs has been updated!', <GiConfirmed />, '#D1FFBD')
        break;
      case 'tone':
        console.log("Saving tone: ", tone)
        await setFAQ("tone", tone)
        openPopup('Tone Settings Saved.', 'Tone has been updated!', <GiConfirmed />, '#D1FFBD')
        break;
      case 'name':
        console.log("Saving brand: ", brand)
        await setFAQ("name", brand)
        // Refresh AuthProvider's brands list so sidebar / brand picker show the new name without a full reload
        await triggerBrand()
        openPopup('Brand Settings Saved.', 'Brand name has been updated!', <GiConfirmed />, '#D1FFBD')
        break;
      default:
        break;
    }
    if (AUDITED_REVIEW_FIELDS.includes(boxName)) {
      setCheckedOff(prev => ({ ...prev, [boxName]: null }));
      // Saving an audited field re-adds it to pending — refresh the sidebar so it
      // reappears immediately rather than waiting for the next poll.
      refreshPendingReviews((brands || []).map(b => b.id));
    }
  };


  const [historyDrawer, setHistoryDrawer] = useState({ open: false, field: null, label: '' });
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (field, label) => {
    setHistoryDrawer({ open: true, field, label });
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const res = await fieldHistory(field);
      console.log("fieldHistory response:", res);
      setHistoryData(res?.history || []);
    } catch (e) {
      console.error("Error loading field history:", e);
    }
    setHistoryLoading(false);
  };

  const [checkedOff, setCheckedOff] = useState({});
  const [checkOffLoading, setCheckOffLoading] = useState({});

  const handleCheckOff = async (field, label) => {
    setCheckOffLoading(prev => ({ ...prev, [field]: true }));
    try {
      await checkOffField(field);
      // Field is no longer pending the moment the server write succeeds — refresh
      // the sidebar now, before (and independent of) the local checkedOff resync.
      refreshPendingReviews((brands || []).map(b => b.id));
      openPopup(`${label} Checked Off`, `${label} has been approved.`, <GiConfirmed />, '#D1FFBD');
      // Local UI resync is best-effort: a failure here must not report the
      // (already successful) check-off as failed.
      try {
        const values = await brandValues(['checkedOff']);
        setCheckedOff(values.checkedOff || {});
      } catch (syncErr) {
        console.error('checkedOff resync failed', syncErr);
      }
    } catch (e) {
      const msg = e?.response?.data?.error || 'Could not check off this field.';
      openPopup('Check-off failed', msg, <GrCircleAlert />, '#FFD6D6');
    }
    setCheckOffLoading(prev => ({ ...prev, [field]: false }));
  };

  const [automations, setAutomations2] = useState({});


  const [stripeLink, setStripeLink] = useState('');

  // const handleAiToggle = async (checked) => {
  //   // console.log("handle AI toggle ", checked);
  //   // setAiEnabled(checked); // Update the state immediately

  //   if (checked) { // true
  //     // Call your async functions here when AI is turned ON
  //     try {
  //       // Make sure the user has a Stripe account to make sure we charge them, and if not then
  //       // pull up a screen that makes them create an account
  //       const create = await stripeAccount();
  //       console.log("Retrieved Stripe account status: ", create);
  //       console.log("Create Message: ", create.message)
  //       console.log("Create Type: ", create.type)

  //       setStripeMessage(create?.message || "Manage your Stripe account..")
  //       setStripeType(create?.type || "error")

  //       if (create.status === true) {
  //         console.log("Stripe is already set and we're ready to roll!");
  //         setAiEnabled(true); // Update the state when the account is successfully created
  //         openPopup('AI Enabled.', 'AI is set to Active', <GrCircleAlert />, '#EEE')
  //         await setBrandValues([{ name: "ai", value: true }]);

  //       } else {
  //         // Send a popup that makes them create a Stripe account
  //         console.log("Send a popup to create the Stripe account");
  //         setAiEnabled(false);
  //         await setBrandValues([{ name: "ai", value: false }]);
  //         setIsModalVisible(!isModalVisible);

  //         if (create.type === 'billing') {
  //           setStripeLink('https://billing.stripe.com/p/login/3csaIj88Dcrn8j63cc')
  //         } else if (create.type === 'plan') {
  //           const subscriptionLink = await generateSubscriptionLink();
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error calling async functions:', error);
  //       setAiEnabled(false);
  //       await setBrandValues([{ name: "ai", value: false }]);
  //     }
  //   } else {
  //     // If the user toggles AI OFF, you can set aiEnabled to false immediately
  //     openPopup('AI Disabled.', 'AI is set to Inactive', <GrCircleAlert />, '#EEE')
  //     setAiEnabled(false);
  //     await setBrandValues([{ name: "ai", value: false }]);
  //   }
  // };



  const handleAutomationsToggle = async (checked) => {
    console.log("handle automations toggle ", checked);

    // Conditionally render specific popup
    if (checked) {
      openPopup('Automations Enabled.', 'Automations is set to Active', <GrCircleAlert />, '#EEE')
    } else {
      openPopup('Automations Disabled.', 'Automations is set to Inactive', <GrCircleAlert />, '#EEE')
    }

    // Check if automationsEnabled is different from checked
    // if (automationsEnabled !== checked) {
    setAutomationsEnabled(checked); // Update the state immediately

    try {
      // Update the "automation" field only if there is a change
      await setBrandValues([{ name: "automation", value: checked }]);
    } catch (error) {
      console.log("Error trying to send automation status");
      console.log("Error: ", error);
    }
    // }
  };

  /** Hydrate toggle from server on load — no popup or redundant save. */
  const syncAutomationsFromBrand = (value) => {
    const on = value === true;
    setAutomationsEnabled(on);
  };

  // Define an async function to call brandValues
  const ai = async () => {
    if (brandId) {
      try {
        const data = await brandValues(['automation']); // they'll exist or they won't

        // Handle the data as needed, e.g., set it to state
        console.log('AI Data fetched: ', data);

        if (data.automation !== undefined && data.automation !== null) {
          console.log("Set automation: ", data.automation)
          syncAutomationsFromBrand(data.automation);
        } else {
          syncAutomationsFromBrand(false);
        }
      } catch (error) {
        console.error('Error fetching AI data:', error);
      }
    }
  };


  useEffect(() => {
    ai();

    fetchKeys();
    fetchGorgiasKeys();
    // Call the getAutomations function here to fetch the automation values
    getAutomations()
      .then((data) => {
        // console.log("getAutomations useEffect in settings is: ", data)
        setAutomations2(data)
      })
      .catch((error) => console.error('Error occurred while getting automations:', error));
  }, [brandId]);


  useEffect(() => {
    latestBrandIdRef.current = brandId;
  }, [brandId]);

  useEffect(() => {
    async function fetchData() {
      const requestBrandId = brandId;
      setBrandSettingsHydratedFor(null);
      try {
        const values = await brandValues(['faqs', 'name', 'policy', 'tone', 'arguments', 'testmode', 'automation', 'checkedOff']);
        console.log("Retrieved brand values: ", values);

        if (latestBrandIdRef.current !== requestBrandId) {
          return;
        }

        setPolicy(values.policy);
        setFaqNew(values.faqs);
        setTone(values.tone);
        setBrand(values.name);
        setTestMode(values.testmode ?? true);
        setCheckedOff(values.checkedOff || {});
        setBrandSettingsHydratedFor(requestBrandId);

        // Update the automation value in the args state
        setArgs(prevArgs => {
          const updatedArgs = prevArgs.map(arg => {
            if (arg.id === 'automations') {
              return {
                ...arg,
                automation: values.automation
              };
            }
            return arg;
          });
          return updatedArgs;
        });
      } catch (error) {
        console.log("Error: ", error);
      }
    }

    if (brandId !== null) {
      fetchData();
    } else {
      setBrandSettingsHydratedFor(null);
    }
    // fetchData();
  }, [brandId]);

  useEffect(() => {
    const field = getFieldParam(location.search);
    if (!field) {
      handledFieldFocusRef.current = null;
      return;
    }

    const focusKey = `${brandId || 'no-brand'}:${field}:${location.key || location.state?.scrollTs || location.search}`;

    if (activeTab !== 'policy') {
      setActiveTab('policy');
      return;
    }

    if (!brandId || brandSettingsHydratedFor !== brandId) {
      return;
    }

    if (handledFieldFocusRef.current === focusKey) {
      return;
    }
    handledFieldFocusRef.current = focusKey;

    let retryTimer = null;
    let frameOne = null;
    let frameTwo = null;

    const clearFieldParam = () => {
      const params = new URLSearchParams(location.search);
      params.delete('field');
      const nextSearch = params.toString();
      history.replace({
        pathname: '/',
        search: nextSearch ? `?${nextSearch}` : '',
      });
    };

    const focusTarget = (shouldRetry = true) => {
      const target = document.getElementById(`field-${field}`);
      if (!target) {
        clearFieldParam();
        return;
      }

      if (shouldRetry && target.getBoundingClientRect().height === 0) {
        retryTimer = window.setTimeout(() => focusTarget(false), 75);
        return;
      }

      clearFieldParam();
      window.setTimeout(() => {
        const currentTarget = document.getElementById(`field-${field}`);
        if (!currentTarget) return;

        currentTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });

        const textarea = currentTarget.querySelector('textarea');
        if (textarea) {
          try {
            textarea.focus({ preventScroll: true });
          } catch {
            textarea.focus();
          }
        }
      }, 250);
    };

    frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(() => focusTarget(true));
    });

    return () => {
      if (frameOne) window.cancelAnimationFrame(frameOne);
      if (frameTwo) window.cancelAnimationFrame(frameTwo);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [location.search, location.key, location.state?.scrollTs, brandId, brandSettingsHydratedFor, activeTab, history]);


  const [passwordVisible, setPasswordVisible] = useState(false);

  function isObjectEmpty(obj) {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        return true; // Object has at least one key, so it's not empty
      }
    }
    return false; // Object has no keys, so it's empty
  }

  const fetchKeys = async () => {
    // Call your function here to fetch the keys
    // console.log("Fetching keys..")
    const theKeys = await getKeys();
    // Update the state variables with the fetched keys
    setShopifyName(theKeys.shopify_name || '');
    setShopifyApiKey(theKeys.shopify_api_key || '');
    setShopifyPrivateKey(theKeys.shopify_private_key || '');
    setShopifyAdmin(theKeys.shopify_admin || '');

    const hasKeys = isObjectEmpty(theKeys);
    if (hasKeys) {
      setAiLock(false);
    } else {
      setAiLock(true);
    }
  };


  const fetchGorgiasKeys = async () => {
    // Call your function here to fetch the keys
    const keys = await getGorgiasKeys();
    // Update the state variables with the fetched keys
    setGorgiasName(keys.gorgias_name || '');
    setGorgiasEmail(keys.gorgias_email || '');
    setGorgiasApi(keys.gorgias_api || '');

    const hasKeys = isObjectEmpty(keys);
    if (hasKeys) {
      setAiLock(false);
    } else {
      setAiLock(true);
    }
  };

  const handleShowCodeClick = async () => {
    // await fetchKeys();
    setShowCode(true);
  };

  const handleShowCodeClickGorgias = async () => {
    setShowCodeGorgias(true);
  };

  const handleShowCodeClickChirpish = async () => {
    setShowCodeChirpish(true);
  };

  const saveKeys = async () => {
    try {
      const data = {
        shopify_name: shopifyName,
        shopify_api_key: shopifyApiKey,
        shopify_private_key: shopifyPrivateKey,
        shopify_admin: shopifyAdmin,
      };

      await sendApiKeys(data);
      openPopup('API Keys Saved.', 'API Keys Added / Updated!', <GiConfirmed />, '#D1FFBD')
      setAiLock(false);
      setShowCode(false);
    } catch (error) {
      openPopup('Saved Failed.', 'Failed Saving API Keys.', <GiConfirmed />, '#D1FFBD')
      console.log("Error sending API Keys.. ", error)
    }
  };


  const saveKeysGorgias = async () => {
    try {
      const data = {
        gorgias_name: gorgiasName,
        gorgias_email: gorgiasEmail,
        gorgias_api: gorgiasApi,
      };

      await gorgiasKeys(data);
      openPopup('API Keys Saved.', 'API Keys Added / Updated!', <GiConfirmed />, '#D1FFBD')
      setAiLock(false);
      setShowCodeGorgias(false);
    } catch (error) {
      openPopup('Saved Failed.', 'Failed Saving API Keys.', <GiConfirmed />, '#D1FFBD')
      console.log("Error sending API Keys.. ", error)
    }
  };




  const [shopifyName, setShopifyName] = useState('');
  const [shopifyApiKey, setShopifyApiKey] = useState('');
  const [shopifyPrivateKey, setShopifyPrivateKey] = useState('');
  const [shopifyAdmin, setShopifyAdmin] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [agentName, setAgentName] = useState('');

  const [gorgiasName, setGorgiasName] = useState('');
  const [gorgiasEmail, setGorgiasEmail] = useState('');
  const [gorgiasApi, setGorgiasApi] = useState('');
  const [chirpishApi, setChirpishApi] = useState('');

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [saveButtonDisabledGorgias, setSaveButtonDisabledGorgias] = useState(true);

  useEffect(() => {
    const areAllInputsFilled =
      shopifyName.trim() !== '' &&
      shopifyApiKey.trim() !== '' &&
      shopifyPrivateKey.trim() !== '' &&
      shopifyAdmin.trim() !== '';

    setSaveButtonDisabled(!areAllInputsFilled);
  }, [shopifyName, shopifyApiKey, shopifyPrivateKey, shopifyAdmin, brandId]);


  // useEffect(() => {
  //   const areAllInputsFilled2 =
  //     gorgiasName.trim() !== '' &&
  //     gorgiasEmail.trim() !== '' &&
  //     gorgiasApi.trim() !== '' &&

  //   setSaveButtonDisabledGorgias(!areAllInputsFilled2);
  // }, [gorgiasName, gorgiasEmail, gorgiasApi, brandId]);

  useEffect(() => {
    const areAllInputsFilled2 =
      gorgiasName.trim() !== '' &&
      gorgiasEmail.trim() !== '' &&
      gorgiasApi.trim() !== '';

    setSaveButtonDisabledGorgias(!areAllInputsFilled2);
  }, [gorgiasName, gorgiasEmail, gorgiasApi, brandId]);


  const [currentBrand, setCurrentBrand] = useState({ id: null, name: null });

  useEffect(() => {
    const matchedBrand = brands?.find(brand => brand.id === brandId);
    if (matchedBrand) {
      setCurrentBrand(matchedBrand);
    } else {
      setCurrentBrand({ id: null, name: null });
    }
  }, [brandId, brands]);

  // Define state variables for each switch
  const [refundEnabled, setRefundEnabled] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [faqsEnabled, setFaqsEnabled] = useState(false);
  const [modifyEnabled, setModifyEnabled] = useState(false);
  const [cancellationsEnabled, setCancellationsEnabled] = useState(false);


  useEffect(() => {
    // Update automation values based on state values
    // console.log("Updating the automation values")
    // console.log("args: ", args)

    const updatedPresets = args.map(preset => {
      switch (preset.title) {
        case "Automations":
          return { ...preset, automation: automationsEnabled };
        case "Refunds":
          return { ...preset, automation: refundEnabled };
        case "Cancellation":
          return { ...preset, automation: cancellationsEnabled };
        case "Tracking":
          return { ...preset, automation: trackingEnabled };
        case "Modify":
          return { ...preset, automation: modifyEnabled };
        case "FAQs":
          return { ...preset, automation: faqsEnabled };
        default:
          return preset;
      }
    });

    // console.log("updatedPresets : ", updatedPresets)
    setArgs(updatedPresets);
  }, [refundEnabled, cancellationsEnabled, trackingEnabled, modifyEnabled, faqsEnabled]);


  // Function to handle switch changes with a key
  const handleSwitchChange = async (key, newValue) => {
    // Update the state variable based on the key
    console.log("handleSwitchChange", key, newValue);
    switch (key) {
      case 'automation':
        handleAutomationsToggle(newValue);
        break;
      case 'refund':
        setRefundEnabled(newValue);
        break;
      case 'tracking':
        setTrackingEnabled(newValue);
        break;
      case 'faqs':
        setFaqsEnabled(newValue);
        break;
      case 'modify':
        setModifyEnabled(newValue);
        break;
      case 'cancellations':
        setCancellationsEnabled(newValue);
        break;
      default:
        const updatedArgs = [...args];
        console.log("new value is: ", newValue);
        console.log("Updated arg before: ", updatedArgs[key]);
        updatedArgs[key].automation = newValue;
        console.log("Updated arg after: ", updatedArgs[key]);
        handleSaveButton(updatedArgs[key]);
        setArgs(updatedArgs);
        break;
    }

    // Update the brandValues object in Firestore
    const updatedBrandValues = {
      automationValues: {
        refund: key === 'refund' ? newValue : refundEnabled,
        tracking: key === 'tracking' ? newValue : trackingEnabled,
        faq: key === 'faqs' ? newValue : faqsEnabled,
        modify: key === 'modify' ? newValue : modifyEnabled,
        cancellation: key === 'cancellations' ? newValue : cancellationsEnabled,
      },
    };
    await setBrandValues([{ name: "automationValues", value: updatedBrandValues.automationValues }]);
  };


  useEffect(() => {
    // Fetch brandValues data and set the state variables
    const fetchData = async () => {
      const data = await brandValues(['automationValues']);
      // console.log("Data from automation values: ", data);

      if (data && data.automationValues) {
        // console.log("automation values 1")
        const { refund, tracking, faq, cancellation, modify } = data.automationValues;

        // Set state variables with default values of false if the properties don't exist
        setRefundEnabled(refund !== undefined ? refund : false);
        setTrackingEnabled(tracking !== undefined ? tracking : false);
        setFaqsEnabled(faq !== undefined ? faq : false);
        setModifyEnabled(modify !== undefined ? modify : false);
        setCancellationsEnabled(cancellation !== undefined ? cancellation : false);
      } else {
        console.log("automation values 2")
        // If data.automationValues doesn't exist, set all state variables to false
        setRefundEnabled(false);
        setTrackingEnabled(false);
        setFaqsEnabled(false);
        setModifyEnabled(false);
        setCancellationsEnabled(false);
      }
    };

    if (brandId !== null) {
      fetchData();
    }
    // fetchData();
  }, [brandId]);



  const openPopup = (message, description, icon, bg) => {
    notification.open({
      message,
      description,
      icon,
      placement: 'topRight',
      style: { 'background-color': bg },
      duration: 4,
    })
  }


  const handleToggleArguments = (index) => {
    setSelectedOption2(index);
  };


  const [selectedOption2, setSelectedOption2] = useState(0);

  // Handle delete for the already existing automations arguments
  const handleDeleteArgument = (automationIndex, argumentIndex) => {
    console.log("handleDeleteArgument")
    console.log("automationIndex", automationIndex)
    console.log("argumentIndex", argumentIndex)

    // Log args at the specified automationIndex
    const argsAtIndex = args[automationIndex];
    console.log("args at automationIndex:", argsAtIndex);

    // Log the argument object at the specified argumentIndex
    const argumentObject = argsAtIndex.arguments[argumentIndex];
    console.log("argument object:", argumentObject);

    setArgs(prevArgs => {
      const updatedArgs = [...prevArgs];
      const updatedArguments = [...updatedArgs[automationIndex].arguments];
      updatedArguments.splice(argumentIndex, 1);
      updatedArgs[automationIndex] = { ...updatedArgs[automationIndex], arguments: updatedArguments };
      return updatedArgs;
    });
  };




  const [newIntegration, setNewIntegration] = useState({
    name: '',
    description: '',
    builtIn: false,
    automation: true,
    webhook: '',
    arguments: [],
  });

  const [keyInput, setKeyInput] = useState('');
  const [valueInput, setValueInput] = useState('');


  // Handle adding argument objects
  const handleAdd = () => {
    // Create a new argument object
    const newArgument = {
      arg: keyInput,
      value: valueInput,
    };

    // Update the state by appending the new argument to the existing arguments array
    setNewIntegration(prevIntegration => ({
      ...prevIntegration,
      arguments: [...prevIntegration.arguments, newArgument],
    }));

    // Optionally, you can clear the key and value inputs
    setKeyInput('');
    setValueInput('');
  };


  // Handle adding argument objects
  const handleAddArguments = (index) => {
    const newArgument = {
      arg: keyInput,
      value: valueInput,
    };

    setArgs(prevArgs => {
      const updatedArgs = [...prevArgs];
      const updatedArguments = [...updatedArgs[index].arguments, newArgument];
      updatedArgs[index] = { ...updatedArgs[index], arguments: updatedArguments };
      return updatedArgs;
    });

    // Optionally, you can clear the key and value inputs
    setKeyInput('');
    setValueInput('');
  };


  // Handle deleting
  const handleDeleteArg = (argumentIndex) => {
    setNewIntegration(prevIntegration => {
      const updatedArguments = [...prevIntegration.arguments];
      updatedArguments.splice(argumentIndex, 1);
      return { ...prevIntegration, arguments: updatedArguments };
    });
  };


  // Change the toggle of whether the automation should be on or not
  const handleToggleAutomation = (index, value, automation) => {
    console.log("handleToggleAutomation ", index, value, automation);
    // console.log("args: ", args)

    if ((index >= 0) && (index < 6)) {
      if (index === 0) {
        handleSwitchChange('automation', value)
      } else if (index === 1) {
        handleSwitchChange('refund', value)
      } else if (index === 2) {
        handleSwitchChange('cancellations', value)
      } else if (index === 3) {
        handleSwitchChange('tracking', value)
      } else if (index === 4) {
        handleSwitchChange('faqs', value)
      } else if (index === 5) {
        handleSwitchChange('modify', value)
      }
    } else {
      console.log("About to handleSwitchChange");
      handleSwitchChange(index, value);
    }

    setArgs((prevArgs) => {
      const updatedArgs = [...prevArgs];
      updatedArgs[index].automation = !updatedArgs[index].automation;
      return updatedArgs;
    });
  };


  const handleAddIntegration = async () => {
    // Your logic for handling the button press goes here
    // For example, you can submit the form or perform any other action
    console.log("Add Integration button pressed!: ", newIntegration);

    // Generate a new UUID for the ID of the newIntegration object
    const newId = uuidv4();

    // Set the UUID as the ID for the newIntegration object
    const newIntegrationWithId = {
      ...newIntegration,
      id: newId
    };


    try {
      const response = await addIntegration(newIntegrationWithId);
      console.log("Adding integration.. ", response)
      if (response.success === true) {
        // Append the new integration to the existing array
        openPopup('Added Integration.', 'Successfully added new integration.', <GiConfirmed />, '#D1FFBD')
        setArgs(prevArgs => [...prevArgs, newIntegrationWithId]);
      }
      // await fetchIntegrations();
    } catch (error) {
      console.log("Error trying to write integration: ", error)
    }
  };


  const handleSaveButton = async (automation) => {
    try {
      // Use the "id" from the clicked object
      const { id } = automation;
      console.log(`Save button clicked for object with ID: ${id}`);

      // Find the object in the 'args' array that matches the provided 'id'
      const matchingObject = args.find(obj => obj.id === id);

      if (matchingObject) {
        // Handle the matching object as needed
        console.log(`Save button clicked for object with ID: ${id}`);
        console.log('Matching Object:', matchingObject);
        await saveIntegration(matchingObject, id);
        openPopup('Saved Integration.', 'Successfully saved integration.', <GiConfirmed />, '#D1FFBD')
        // Perform additional logic or update the object as needed
      } else {
        console.log(`No object found with ID: ${id}`);
      }
      // You can perform additional logic or save the data as needed
    } catch (error) {
      console.log("Error: ", error)
    }
  };


  const handleDeleteButton = async (automation) => {
    try {
      // Use the "id" from the clicked object
      const { id } = automation;
      console.log(`Delete button clicked for object with ID: ${id}`);

      // Call the function to delete the corresponding document from Firestore
      const deleteResponse = await deleteIntegration(id);

      if (deleteResponse.success) {
        // Find the index of the object with the matching ID in 'args'
        const indexToDelete = args.findIndex(obj => obj.id === id);

        if (indexToDelete !== -1) {
          // Remove the object from 'args' array
          const updatedArgs = [...args];
          updatedArgs.splice(indexToDelete, 1);
          setArgs(updatedArgs);

          openPopup('Deleted Integration.', 'Successfully deleted integration.', <GiConfirmed />, '#D1FFBD')
          console.log(`Object with ID ${id} deleted from args array`);
        } else {
          console.log(`No object found with ID: ${id}`);
        }
      } else {
        console.error(`Error deleting integration with ID: ${id}`);
      }
    } catch (error) {
      console.error("Error handling delete button:", error);
    }
  }


  const saveChirpishApi = async () => {
    try {
      const response = await createApi(gorgiasDomain, gorgiasAdminEmail, gorgiasApiKey);
      if (response) {
        setAuthorizationKey(response);
      }
    } catch (error) {
      console.error('Error creating Gorgias API key:', error);
    }
  };



  const [showCodeTrack, setShowCodeTrack] = useState(false);
  const [trackKey, setTrackKey] = useState(''); // UseState for your API key
  // const [trackValue, setTrackValue] = useState(''); // Value for your API key input

  useEffect(() => {
    setShowCode(false);
    setShowCodeGorgias(false);
    setShowCodeChirpish(false);
    setShowCodeTrack(false);
  }, [brandId]);

  const loadZendeskOAuth = async () => {
    const data = await getZendeskOAuth();
    setZendeskOAuthState(data && data.configured ? data : null);
    setZendeskSubdomain(data && data.configured ? (data.subdomain || '') : '');
    setZendeskClientSecret('');
    setZendeskWebhookSecret('');
    setZendeskClientId('');
  };

  useEffect(() => {
    if (!brandId) return;
    setZendeskMessage('');
    setZendeskNewSecret('');
    loadZendeskOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const copyZendeskValue = async (text, successDescription) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      message.success(successDescription)
    } catch {
      message.error("Could not copy")
    }
  }

  // One labelled, read-only, copyable value. Used for every string the co-worker
  // has to paste into Zendesk, so none of them has to be retyped by hand.
  const zendeskCopyField = (label, value, hint) => (
    <div style={{marginBottom: 16}}>
      <p className='key-title' style={{marginBottom: 4}}>{label}</p>
      <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
        <Input readOnly size="small" value={value} />
        <Button
          size="small"
          icon={<CopyOutlined />}
          style={{flexShrink: 0}}
          onClick={() => copyZendeskValue(value, `${label} copied`)}
        >
          Copy
        </Button>
      </div>
      {hint && <p className="drop-text" style={{marginTop: 4, marginBottom: 0}}>{hint}</p>}
    </div>
  );

  const saveZendeskOAuth = async (status, {regenerateWebhookSecret = false} = {}) => {
    setZendeskSaving(true);
    setZendeskMessage('');
    setZendeskNewSecret('');

    const response = await setZendeskOAuth({
      subdomain: zendeskSubdomain,
      clientId: zendeskClientId,
      clientSecret: zendeskClientSecret,
      webhookSecret: zendeskWebhookSecret,
      status,
      regenerateWebhookSecret,
    });

    setZendeskSaving(false);
    setZendeskMessage(response?.message || (response?.success ? 'Saved.' : 'Could not save.'));

    if (response?.generatedWebhookSecret) setZendeskNewSecret(response.generatedWebhookSecret);
    if (response?.success) await loadZendeskOAuth();
  };

  const handleShowCodeClickTrack = () => {
    setShowCodeTrack(true);
  };


  const saveKeysTrack = async () => {
    // Your save keys function here
    try {
      await trackingKey(trackKey);
      openPopup('API Keys Saved.', 'API Keys Added / Updated!', <GiConfirmed />, '#D1FFBD')
      setAiLock(false);
      setShowCodeTrack(false);
    } catch (error) {
      openPopup('Saved Failed.', 'Failed Saving API Keys.', <GiConfirmed />, '#D1FFBD')
      console.log("Error sending API Keys.. ", error)
    }
  };


  useEffect(() => {
    const fetchData3 = async () => {
      try {
        const tracking = await getTrackingKey();
        if (tracking) {
          setTrackKey(tracking);
        } else {
          setTrackKey("");
        }
      } catch (error) {
        console.log("Error setting API key.");
      }
    };

    fetchData3();
  }, [currentUser, brandId]);



  const handleToggleTestMode = () => {
    // Update testMode in the database (assuming you have a function for this)
    setFAQ("testmode", !testMode)

    // Update testMode in the local state
    setTestMode(prevTestMode => !prevTestMode);
  };


  const [clients, setClients] = useState([]);
  const [brandsDisclosureOpenByClientId, setBrandsDisclosureOpenByClientId] = useState({});
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [modalEditingClientId, setModalEditingClientId] = useState(null);
  const [addClientForm] = Form.useForm();
  const [clientModalBrandSearch, setClientModalBrandSearch] = useState('');

  const brandIdsFromSelection = (selection) => {
    if (!selection || selection.length === 0) return [];
    if (selection.includes('all')) {
      return (brands || []).map((b) => b.id);
    }
    return selection.filter((v) => v !== 'all');
  };

  /** Values for the brand multi-select when opening the modal for an existing client */
  const formBrandIdsForSelect = (clientBrandIds) => {
    const ids = clientBrandIds || [];
    const allIds = (brands || []).map((b) => b.id);
    if (
      allIds.length > 0
      && ids.length === allIds.length
      && allIds.every((id) => ids.includes(id))
    ) {
      return ['all'];
    }
    return ids;
  };

  const closeClientModal = () => {
    setAddClientModalOpen(false);
    setModalEditingClientId(null);
    setClientModalBrandSearch('');
    addClientForm.resetFields();
  };

  const openEditClientModal = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    setModalEditingClientId(clientId);
    setClientModalBrandSearch('');
    addClientForm.setFieldsValue({
      name: client.name,
      brandIds: formBrandIdsForSelect(client.brandIds),
    });
    setAddClientModalOpen(true);
  };

  const handleClientModalSubmit = async () => {
    try {
      const values = await addClientForm.validateFields();
      const name = values.name.trim();
      if (!name) return;
      const brandIds = normalizeBrandIds(brandIdsFromSelection(values.brandIds || []));

      if (modalEditingClientId) {
        const existing = clients.find((c) => c.id === modalEditingClientId);
        if (!existing) return;
        const updatedClient = { ...existing, name, brandIds };
        const response = await addClient(updatedClient);
        console.log('Response from saving client: ', response);

        if (response.success === true) {
          setClients(
            clients.map((c) => (c.id === modalEditingClientId ? updatedClient : c)),
          );
          closeClientModal();
        } else {
          openPopup('Update failed', response?.error || 'You do not have permission to edit clients.', <GrCircleAlert />, '#FFD6D6');
        }
      } else {
        const newClient = {
          id: Date.now().toString(),
          name,
          brandIds,
        };

        const response = await addClient(newClient);
        console.log('Response from adding new client: ', response);

        if (response.success === true) {
          setClients([...clients, newClient]);
          closeClientModal();
        } else {
          openPopup('Update failed', response?.error || 'You do not have permission to edit clients.', <GrCircleAlert />, '#FFD6D6');
        }
      }
    } catch (e) {
      if (e?.errorFields) return;
      console.error(e);
    }
  };

  const handleDeleteClient = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    Modal.confirm({
      title: 'Delete this client?',
      content: client
        ? `This will permanently remove "${client.name}". This cannot be undone.`
        : 'This client will be permanently removed.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const response = await deleteClient(clientId);
        console.log('Response from deleting client: ', response);
        if (response?.success === true) {
          setClients((prev) => prev.filter((c) => c.id !== clientId));
        } else {
          openPopup('Delete failed', response?.error || 'You do not have permission to edit clients.', <GrCircleAlert />, '#FFD6D6');
        }
      },
    });
  };

  useEffect(() => {
    // Fetch clients from the server when the component mounts
    fetchClients();
  }, []);


  const fetchClients = async () => {
    try {
      const response = await getClients();
      // console.log(`Fetched clients: `, response);
      setClients(response);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const clientsWithBrandLists = clients.filter((c) => (c.brandIds || []).length > 0);
  const allBrandDisclosuresExpanded =
    clientsWithBrandLists.length > 0
    && clientsWithBrandLists.every((c) => brandsDisclosureOpenByClientId[c.id] === true);

  const toggleAllClientBrandDisclosures = () => {
    if (clientsWithBrandLists.length === 0) return;
    if (allBrandDisclosuresExpanded) {
      setBrandsDisclosureOpenByClientId({});
    } else {
      setBrandsDisclosureOpenByClientId(
        Object.fromEntries(clientsWithBrandLists.map((c) => [c.id, true])),
      );
    }
  };





  const CheckOffBadge = ({ field, label }) => {
    const userEmail = currentUser?.email;

    // Three-state logic:
    // - key absent from checkedOff → never edited under audit, show nothing
    // - key present with value null → edited and pending review
    // - key present with { by, at } → checked off / approved
    if (!(field in checkedOff)) return null;

    const status = checkedOff[field];

    if (status && status.by) {
      const date = status.at?._seconds
        ? new Date(status.at._seconds * 1000)
        : null;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, minHeight: 24 }}>
          <CheckCircleOutlined style={{ color: '#389e0d' }} />
          <span style={{ color: '#389e0d' }}>Checked off</span>
          {' '}<span style={{ color: '#888' }}>{'('}by {status.by}
          {date && <>{' '}on {date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}{' '}
          {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</>}
          {')'}</span>
        </span>
      );
    }

    return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Button
          size="small"
          type="default"
          className="settings-panel-btn settings-history-btn"
          loading={checkOffLoading[field]}
          onClick={() => handleCheckOff(field, label)}
        >
          Check off
        </Button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#d48806', fontSize: 13, minHeight: 24 }}>
          <ClockCircleOutlined />
          Pending review
        </span>
      </span>
    );
  };

  return (
    <>
      <div className='settings-container'>

        {/* <h1>Onboarding</h1> */}
        {/* <br></br> */}
        {/* <p className="gray">If you want to rewatch the onboarding again you can access it <a href="https://youtu.be/KJBwZtIo4xo" className="hyperlink" target="_blank">here</a>. To book a call with our team click <a href="https://calendly.com/chirpish/demo" className="hyperlink" target="_blank">here</a>.</p> */}
        {/* <div className='team'>
          <h1>Manage team</h1>

          <Button className='add' onClick={showModal}>
            Invite team member
          </Button>

          <Modal title="Add to team" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
            <Input placeholder='Enter Email Address' type="text" id="name" value={emailAddress} onChange={(event) => setEmailAddress(event.target.value)} />
          </Modal>
          <Modal
            title={stripeType === 'billing' ? 'Manage your Subscription' : 'Start your subscription'}
            open={isModalVisible}
            onCancel={toggleModal}
            footer={[
              <Button key="create" type="default" onClick={redirectToStripe}>
                Proceed
              </Button>,
              <Button key="close" onClick={toggleModal}>
                Close
              </Button>,
            ]}
          >
            {stripeType === 'billing' ? (
              <>
                <p>Your free trial of Icarus AI is over. To continue, simply add a payment method to your Stripe account and the billing will start tracking.</p>
                <br></br>
                <p>Clicking 'Proceed' will direct you to a Stripe page that will give you access to your account.</p>
                <br></br>
                <p>If you need help please reach out to our team in the bottom right and we'll respond ASAP.</p>
                <br />
              </>
            ) : (
              <>
                <p>Add a subscription to turn on AI for your brand. Only $0.33/message generated, no surprises.</p>
              </>
            )}
          </Modal>

        </div> */}

        <Tabs
          className="settings-page-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          destroyInactiveTabPane={false}
          items={[
            {
              key: 'brand',
              label: 'Brand',
              children: (
                <div className="selectors settings-tab-panel">
                  <BrandSelectionCard
                    brandName={brand}
                    onBrandNameChange={setBrand}
                    onSaveBrandName={() => handleSaveButtonClick('name')}
                    brandNameLockStyle={lockSettings}
                    canEditBrandName={true}
                  />
                </div>
              ),
            },
            {
              key: 'automation',
              label: 'Automation',
              children: (
                <div className="selectors settings-tab-panel">
          <div className="brand-workspace">
            <ActiveBrandBlock />
            <Divider className="brand-workspace__divider" />
          </div>
          {/* <h1>AI</h1>
          <p className='margin'><i>Please read information below.</i></p>
          <Switch
            className='switchh'
            style={{ ...aiLockSettings, marginRight: "5px" }}
            checkedChildren="AI ON"
            unCheckedChildren="AI OFF"
            checked={aiEnabled}
            onChange={handleAiToggle}
          />
          <p>AI without automations will generate the best response it can with the given information and should be ready to send in 1-click.
            It will NOT try to perform functionality or automatically send the response to the customer.
          </p> */}

          <h2 className="title-2">Automation</h2>
          {/* <p className='margin'><i>Please read information below.</i></p> */}
          <p>Automations give the AI permissions to perform actions on your behalf through any of your integrated apps and gives it permissions to automatically send emails.
            AI is not perfect and can make mistakes. We are not responsible for repercussions from anything done by the AI.
          </p>

          <div className='automations-section'>
            {args.map((automation, index) => (
              <div key={index} className="automation-item" style={{ opacity: (index === 0 || automationsEnabled) ? 1 : 0.70 }}>
                {/* <p>{index}</p> */}
                <div className="content">
                  <div className={`all-in-line ${index !== selectedOption2 ? 'disabled-option' : ''}`} onClick={() => handleToggleArguments(index)}>
                    <Switch
                      style={lockSettings}
                      checkedChildren=""
                      unCheckedChildren=""
                      checked={automation.automation}
                      onChange={(value) => handleToggleAutomation(index, value, automation)}
                    />
                    <div className='the-text-body'>
                      <div className='left-of-body'>
                        <h4 className='titleee'>{automation.title}</h4>
                        <p className='paraaa'>{automation.description}</p>
                      </div>
                      {automation.builtIn === false && (
                        <div className='right-of-body'>
                          <CaretDownOutlined></CaretDownOutlined>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedOption2 === index && !automation.builtIn && (
                    <>
                      <div>
                        <Input
                          className='inputtt'
                          type="text"
                          value={automation.webhook || ''}
                          placeholder="Zapier Webhook URL"
                          onChange={(e) => {
                            const updatedArgs = [...args];
                            updatedArgs[index].webhook = e.target.value;
                            setArgs(updatedArgs);
                          }}
                        />
                      </div>

                      <p>
                        Add the variables required to run the Zap successfully.
                      </p>

                      <div>
                        {args[index].arguments.map((arg, argIndex) => (
                          <div key={argIndex}>
                            <Input
                              type="text"
                              className='texttt'
                              value={arg.arg}
                              placeholder="Key"
                              onChange={(e) => {
                                const updatedValue = e.target.value.replace(/[^a-zA-Z_]/g, ''); // Remove characters other than letters and underscores
                                const updatedArgs = [...args];
                                updatedArgs[index].arguments[argIndex].arg = updatedValue;
                                setArgs(updatedArgs);
                              }}
                            />
                            <Input
                              type="text"
                              className='texttt'
                              value={arg.value}
                              placeholder="Value"
                              onChange={(e) => {
                                const updatedArgs = [...args];
                                updatedArgs[index].arguments[argIndex].value = e.target.value;
                                setArgs(updatedArgs);
                              }}
                            />

                            <Button type="default" danger onClick={() => handleDeleteArgument(index, argIndex)}>
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div>
                        <Input
                          type="text"
                          className='texttt'
                          value={keyInput}
                          placeholder="Key"
                          onChange={(e) => {
                            const updatedValue = e.target.value.replace(/[^a-zA-Z_]/g, ''); // Remove characters other than letters and underscores
                            setKeyInput(updatedValue);
                          }}
                        />
                        <Input
                          type="text"
                          className='texttt'
                          value={valueInput}
                          placeholder="Value"
                          onChange={(e) => setValueInput(e.target.value)}
                        />
                        <Button onClick={() => handleAddArguments(index)} type="default" disabled={keyInput === "" || valueInput === ""}>+</Button>

                        <div className='save-button'>
                          <Button className='buttonz' onClick={() => handleSaveButton(automation)} type="default">
                            Save
                          </Button>
                          <Button className='buttonz' type="default" danger onClick={() => handleDeleteButton(automation)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="automations-section automations-section--add-integration">
            {selectedOption2 !== -1 ? (
              <button type="button" className="super-edit" onClick={() => handleToggleArguments(-1)}>
                Add new integration
              </button>
            ) : (
              <div className="super-edit-2">
                <Input
                  type="text"
                  className="inputtt"
                  value={newIntegration.title}
                  placeholder="Title"
                  onChange={(e) => {
                    const updatedTitle = e.target.value.replace(/[^a-zA-Z_]/g, ''); // Remove characters other than letters and underscores
                    setNewIntegration({ ...newIntegration, title: updatedTitle });
                  }}
                />
                <Input
                  type="text"
                  className="inputtt"
                  value={newIntegration.description}
                  placeholder="Description"
                  onChange={(e) => {
                    const updatedDescription = e.target.value.slice(0, 70); // Limit the description to 70 characters
                    setNewIntegration({ ...newIntegration, description: updatedDescription });
                  }}
                />
                <Input
                  type="text"
                  className="inputtt"
                  value={newIntegration.webhook}
                  placeholder="Zapier Webhook"
                  onChange={(e) => setNewIntegration({ ...newIntegration, webhook: e.target.value })}
                />

                <div>
                  {newIntegration.arguments.map((arg, argIndex) => (
                    <div key={argIndex}>
                      <Input
                        type="text"
                        className="texttt"
                        value={arg.arg}
                        placeholder="Key"
                        onChange={(e) => {
                          const updatedValue = e.target.value.replace(/[^a-zA-Z_]/g, ''); // Remove characters other than letters and underscores
                          setNewIntegration((prevIntegration) => {
                            const updatedArgs = [...prevIntegration.arguments];
                            updatedArgs[argIndex].arg = updatedValue;
                            return {
                              ...prevIntegration,
                              arguments: updatedArgs,
                            };
                          });
                        }}
                      />
                      <Input
                        type="text"
                        className="texttt"
                        value={arg.value}
                        placeholder="Value"
                        onChange={(e) => {
                          setNewIntegration((prevIntegration) => {
                            const updatedArgs = [...prevIntegration.arguments];
                            updatedArgs[argIndex].value = e.target.value;
                            return {
                              ...prevIntegration,
                              arguments: updatedArgs,
                            };
                          });
                        }}
                      />
                      <Button type="default" danger onClick={() => handleDeleteArg(argIndex)}>
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>

                <div>
                  <Input
                    type="text"
                    className="texttt"
                    value={keyInput}
                    placeholder="Key"
                    onChange={(e) => {
                      const updatedValue = e.target.value.replace(/[^a-zA-Z_]/g, ''); // Remove characters other than letters and underscores
                      setKeyInput(updatedValue);
                    }}
                  />
                  <Input
                    type="text"
                    className="texttt"
                    value={valueInput}
                    placeholder="Value"
                    onChange={(e) => setValueInput(e.target.value)}
                  />
                  <Button onClick={handleAdd} type="default" disabled={keyInput === '' || valueInput === ''}>
                    +
                  </Button>
                </div>

                <Button
                  className="margin-button"
                  onClick={handleAddIntegration}
                  type="default"
                  disabled={newIntegration.title === '' || newIntegration.description === '' || newIntegration.webhook === ''}
                >
                  Add Integration
                </Button>
              </div>
            )}
          </div>
                </div>
              ),
            },
            {
              key: 'policy',
              label: 'Policy',
              children: (
                <div className="selectors settings-tab-panel">
          <div className="brand-workspace">
            <ActiveBrandBlock />
            <Divider className="brand-workspace__divider" />
          </div>
          <div id="field-policy" className='faq' style={lockSettings}>
            <h2 className='title-2'>Policy</h2>
            <p>Include ALL data a team would need to answer all FAQ inquiries</p>
            <TextArea
              rows={12}
              placeholder="Kylie Cosmetics - Customer Policy

                        Brand Overview:
                        Kylie Cosmetics is a leading makeup brand founded by Kylie Jenner. Our mission is to provide high-quality cosmetics that empower individuals to express their unique beauty and confidence. We value our customers and strive to create a positive and seamless shopping experience.

                        Tracking Inquiries and Cases:
                        If you have inquiries about your order's tracking information or encounter any issues with tracking, our dedicated customer support team is here to assist you. Simply reach out to our support channels via email or phone, providing your order details, and we'll promptly investigate and update you on the status.

                        Refunds and Returns:
                        We want you to be completely satisfied with your purchase. If you are not delighted with your Kylie Cosmetics product, please contact our support within 30 days of receiving the order. We will guide you through the return process, and upon receiving the returned item(s) in their original condition, we will issue a refund to your original payment method.

                        Cancellations and Modifications:
                        Should you wish to cancel or modify your order, kindly contact us as soon as possible. We'll do our best to accommodate your request before the order is processed for shipment. Please note that once an order has been dispatched, cancellations and modifications may not be possible.

                        Subscriptions:
                        Our subscription service allows you to receive your favorite Kylie Cosmetics products regularly. You have the flexibility to manage your subscription preferences, including delivery frequency, product selection, and billing information, through your online account. You can easily pause, skip, or cancel your subscription at any time.

                        Product Concerns:
                        At Kylie Cosmetics, we take product quality seriously. If you experience any issues with the products you receive, such as damages or defects, please notify us within 7 days of receipt. We will gladly replace or refund any faulty items.

                        Lost or Missing Packages:
                        In the rare event that your package is lost or missing during transit, please inform us immediately. We will initiate an investigation with the shipping carrier and work to resolve the issue promptly. If the package is confirmed lost, we will either resend your order or issue a full refund.

                        Privacy and Security:
                        Kylie Cosmetics values your privacy and ensures that all customer data is treated with the utmost confidentiality and security. We do not share personal information with third parties, except as required for order fulfillment and customer support.

                        Customer Feedback:
                        We welcome your feedback and suggestions. Your input helps us improve our products and services. Feel free to share your thoughts through our website, social media, or customer support channels.

                        Thank you for choosing Kylie Cosmetics. We are committed to delivering the best beauty experience and appreciate your continued support."
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button type="default" className="settings-panel-btn" onClick={() => handleSaveButtonClick('policy')}>Save</Button>
              <Button type="default" size="small" className="settings-panel-btn settings-history-btn" onClick={() => openHistory('policy', 'Policy')}>View History</Button>
              <CheckOffBadge field="policy" label="Policy" />
            </div>
          </div>


          <div id="field-faqs" className='faq' style={lockSettings}>
            <h4 className='title-2'>FAQ's</h4>
            <p>Our system has been trained to avoid making stuff up at all costs. Because of this, you need to be sure to give the AI the information it needs.</p>
            <TextArea
              rows={12}
              placeholder="Any questions that refer to functionality that needs to be done by our system needs to be in the policy
                        section not the FAQ section. This section is purely for questions that are not related to inquiring about things like
                        tracking, refunds, returns etc. which means its more about shipping times, product questions, and just about anything else.
                        If you find it's not doing a good job at answering FAQ inquiries you should try adding more information here, making it more clear,
                        or making it more concise."
              value={faqNew}
              onChange={(e) => setFaqNew(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button type="default" className="settings-panel-btn" onClick={() => handleSaveButtonClick('faqs')}>Save</Button>
              <Button type="default" size="small" className="settings-panel-btn settings-history-btn" onClick={() => openHistory('faqs', 'FAQs')}>View History</Button>
              <CheckOffBadge field="faqs" label="FAQs" />
            </div>
          </div>


          <div id="field-tone" className='faq' style={lockSettings}>
            <h4 className='title-2'>Tone</h4>
            <p>This is OPTIONAL. We write all emails in a friendly and professional tone, but some may require more customization.</p>
            <TextArea
              rows={6}
              placeholder="Try to end all emails in 'May the fourth be with you' if it makes sense give the context of the email."
              value={tone}
              onChange={(e) => setTone(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button type="default" className="settings-panel-btn" onClick={() => handleSaveButtonClick('tone')}>Save</Button>
              <Button type="default" size="small" className="settings-panel-btn settings-history-btn" onClick={() => openHistory('tone', 'Tone')}>View History</Button>
              <CheckOffBadge field="tone" label="Tone" />
            </div>
          </div>
                </div>
              ),
            },
            {
              key: 'api-keys',
              label: 'API Keys',
              children: (
                <div className="selectors settings-tab-panel">
          <div className="brand-workspace">
            <ActiveBrandBlock />
            <Divider className="brand-workspace__divider" />
          </div>

          <div className='apikeys'>
            <h2 className="title-2">Shopify API Keys</h2>
            <p className="drop-text">Integrate Shopify so we can retrieve data and send refunds, cancellations, etc. automatically. An extremely simple and easy-to-follow tutorial is <a href="https://chirpish.notion.site/Chirpish-Onboarding-4d05dcb29fba45b5b72aa8d0a0d82752" className="hyperlink" target="_blank">here</a>.</p>
            {!showCode && (
              <Button type="default" className="settings-panel-btn" onClick={handleShowCodeClick}>
                Show API Keys
              </Button>
            )}
            {showCode && (
              <>
                <div className='keys'>
                  {generateInput('Shopify Name', "Ex. if https://apple.myshopify.com or https://admin.shopify.com/store/apple/ you'd input apple (Needs to be exact!)", shopifyName, setShopifyName)}
                  {generateInput('Shopify API Key', '48gjekdvunw4939wog9enbn4ep99jg9j', shopifyApiKey, setShopifyApiKey)}
                  {generateInput('Shopify Secret Key', '8561c0971844e9nvdojfijdroijw949hehb', shopifyPrivateKey, setShopifyPrivateKey)}
                  {generateInput('Shopify Admin API Access Token', 'shpat_b4nkrenvoirjfoidnrtbdifgnionn593jjgjf0', shopifyAdmin, setShopifyAdmin)}

                  <Button type="default" className="settings-panel-btn" onClick={saveKeys} disabled={saveButtonDisabled}>Save</Button>
                </div>
              </>
            )}
          </div>

          <Divider className="brand-workspace__divider" />

          {/* <div className='apikeys'>
            <h2 className="title-2">Gorgias API keys</h2>
            <p className="drop-text">Integrate Gorgias for the account the brand is managed under.</p>
            {!showCodeGorgias && (
              <Button type="default" className="settings-panel-btn" onClick={handleShowCodeClickGorgias}>
                Show API Keys
              </Button>
            )}
            {showCodeGorgias && (
              <>
                <div className='keys'>
                  {generateInput('Gorgias name', "e.g. mystore.gorgias.com → Gorgias name is mystore", gorgiasName, setGorgiasName)}
                  {generateInput('Gorgias email', 'Your profile email for Gorgias', gorgiasEmail, setGorgiasEmail)}
                  {generateInput('Gorgias API Key', 'Settings > REST API > API Key', gorgiasApi, setGorgiasApi)}

                  <Button type="default" className="settings-panel-btn" onClick={saveKeysGorgias} disabled={saveButtonDisabledGorgias}>Save</Button>
                </div>
              </>
            )}
          </div> */}


          <div className='apikeys'>
            <h2 className="title-2">17Track API key</h2>
            <p className="drop-text">Integrate with 17 Track to provide the most accurate shipping details to your customer.</p>
            {!showCodeTrack && (
              <Button type="default" className="settings-panel-btn" onClick={handleShowCodeClickTrack}>
                Show API Keys
              </Button>
            )}
            {showCodeTrack && (
              <>
                <div className='keys'>
                  <p className='key-title'>17Track API Key</p>
                  <Input
                    placeholder="Enter your 17Track API key"
                    value={trackKey}
                    onChange={(e) => setTrackKey(e.target.value)}
                    autoComplete="off"
                  /><br /><br />
                  <Button type="default" className="settings-panel-btn" onClick={saveKeysTrack} disabled={!trackKey}>Save</Button>
                </div>
              </>
            )}
          </div>

          {
          // currentUser.uid === 'ejgTdfrqPDUra3KeH3onWs8WN723' // login@chirpish.ai
          // || currentUser.uid === 'ot0JlIiySgUffpMu41MLqBFKDZr1' // cael.stewart@gmail.com
          currentUser.uid === 'MQQtQ0sBv3aUYeUiTY4hjSlpNYG3' // danrebmillenas@gmail.com
          // || currentUser.uid === 'wsSsZycZkuMs99yLLn6KCsmLfx93' // asow@icarusagency.nl
          // || currentUser.uid === '98EBbNw3JrYtn6Ffh3VLcqhyIHf1' // dominique@icarusagency.nl
          // || currentUser.uid === 'xNsP3w5QhmdlVtmIKj9nOcIZPVv2' // shrihan@icarusagency.nl
          // || currentUser.uid === 'gYMz8ihcYANl9EZ8ErSMFmZhm5D2' // cael@icarusai.io
          // || currentUser.uid === 'gPzMNKnzfBOvxt0ly5ZA6NTrGYP2' // paulina@icarusai.io
          || currentUser.uid === '93oQcYOxsVZoFzar53zhBKDML252' ? ( // sander@replyer.io
            <>
            <Divider className="brand-workspace__divider" />
            <div className='apikeys'>
              <h2 className="title-2">Create Authorization Key</h2>
              <p className="drop-text">These Authorization keys authenticate requests from your Gorgias CRM.</p>

              {authorizationKey ? (
                <>
                  <br></br>
                  <p>The authorization key is: {authorizationKey}</p>
                </>
              ) :
                <>
                  {!showCodeChirpish && (
                    <Button type="default" className="settings-panel-btn" onClick={handleShowCodeClickChirpish}>
                      Show API Key
                    </Button>
                  )}

                  {showCodeChirpish && (
                    <>
                      <div className='keys'>
                        <p className='key-title'>Gorgias Domain</p>
                        <Input
                          placeholder="your-gorgias-subdomain"
                          value={gorgiasDomain}
                          onChange={(e) => setGorgiasDomain(e.target.value)}
                        /><br /><br />
                        <p className='key-title'>Gorgias Admin Email</p>

                        <Input
                          placeholder="admin@icarusai.io"
                          value={gorgiasAdminEmail}
                          onChange={(e) => setGorgiasAdminEmail(e.target.value)}
                        /><br /><br />
                        <p className='key-title'>Gorgias API Key</p>

                        <Input
                          placeholder="382904t89h828h92hg9h8294hg334hg04h3983hg4389hg"
                          value={gorgiasApiKey}
                          onChange={(e) => setGorgiasApiKey(e.target.value)}
                        /><br /><br />
                        <Button type="default" className="settings-panel-btn" onClick={saveChirpishApi} disabled={!gorgiasDomain || !gorgiasAdminEmail || !gorgiasApiKey}>Create key</Button>
                      </div>
                    </>
                  )}
                </>
              }
            </div>

            <Divider className="brand-workspace__divider" />
            <div className='apikeys'>
              <h2 className="title-2">Zendesk OAuth</h2>
              <div className='keys'>
                <p className='key-title'>Zendesk subdomain</p>
                <Input
                  placeholder="acme"
                  value={zendeskSubdomain}
                  onChange={(e) => setZendeskSubdomain(e.target.value)}
                  autoComplete="off"
                /><br /><br />

                <p className='key-title'>
                  Client ID {zendeskOAuth?.clientIdConfigured ? '(saved)' : ''}
                </p>
                <Input
                  placeholder={zendeskOAuth?.clientIdConfigured ? 'Leave empty to keep the saved value' : 'Zendesk OAuth client ID'}
                  value={zendeskClientId}
                  onChange={(e) => setZendeskClientId(e.target.value)}
                  autoComplete="off"
                /><br /><br />

                <p className='key-title'>
                  Client secret {zendeskOAuth?.clientSecretConfigured ? '(saved)' : ''}
                </p>
                <Input.Password
                  placeholder={zendeskOAuth?.clientSecretConfigured ? 'Leave empty to keep the saved value' : 'Zendesk OAuth client secret'}
                  value={zendeskClientSecret}
                  onChange={(e) => setZendeskClientSecret(e.target.value)}
                  autoComplete="new-password"
                /><br /><br />

                <p className='key-title'>
                  Webhook secret {zendeskOAuth?.webhookSecretConfigured ? '(saved)' : ''}
                </p>
                <Input.Password
                  placeholder={zendeskOAuth?.webhookSecretConfigured ? 'Leave empty to keep the saved value' : 'Bearer token for the Zendesk webhook'}
                  value={zendeskWebhookSecret}
                  onChange={(e) => setZendeskWebhookSecret(e.target.value)}
                  autoComplete="new-password"
                /><br /><br />

                {!zendeskOAuth && (
                  <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 16}}
                    message="Subdomain first"
                    description={
                      <span>
                        Enter the subdomain (<code>client-xyz</code>, not a URL)
                      </span>
                    }
                  />
                )}

                {zendeskNewSecret && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Copy the webhook secret now"
                    description={
                      <>
                        <p>Shown once - use as the Bearer token in the Zendesk webhook</p>
                        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                          <Input readOnly size="small" value={zendeskNewSecret} />
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            style={{flexShrink: 0}}
                            onClick={() => copyZendeskValue(zendeskNewSecret, 'Webhook secret copied')}
                          >
                            Copy
                          </Button>
                        </div>
                      </>
                    }
                  />
                )}
                <br />

                {zendeskOAuth?.webhookUrl && (
                  <div style={{marginTop: 8, marginBottom: 8}}>
                    <h3 className="title-3">Zendesk setup</h3>

                    <h4>1. Webhook</h4>
                    {zendeskCopyField('Endpoint URL', zendeskOAuth.webhookUrl)}
                    <p className="drop-text">Method POST + format JSON</p>

                    <h4>2. Authentication</h4>
                    <p className="drop-text">
                      Pick <code>Bearer token</code> and use the webhook secret above as value
                    </p>
                    <p className="drop-text">
                      Zendesk also shows its own signing secret (we don't use this!)
                    </p>

                    <h4>3. Group name</h4>
                    {zendeskCopyField(
                      'Append to the Zendesk group name',
                      `id=${brandId}`,
                      'Note: "Support AI id=x" works, "Support AI (id=x)" does not'
                    )}

                    <h4>4. Trigger</h4>
                    {zendeskCopyField(
                      'JSON Payload',
                      '{ "ticket_id": "{{ticket.id}}", "group_name": "{{ticket.group.name}}" }',
                      'group_name is required'
                    )}
                    <p className="drop-text">
                      Add a condition on the trigger: <code>Group is</code> the group from step 3. Keeps tickets without a group out of the queue, and routing should happen in the trigger.
                    </p>

                    <h4>5. Activate here, then update a test ticket.</h4>
                    <p className="drop-text">
                      Start with automation off (also gates Shopify refunds, cancellations, discounts), policy filled in.
                    </p>
                    <p className="drop-text">
                      Processing starts ~2 min after the webhook. Nothing at all? Check missing{' '}
                      <code>group_name</code>, or punctuation after <code>id=</code>.
                    </p>
                  </div>
                )}

                {zendeskOAuth && (
                  <div style={{marginBottom: 16}}>
                    <p className='key-title' style={{marginBottom: 8}}>Connection readiness</p>
                    {[
                      ['Zendesk subdomain', Boolean(zendeskOAuth.subdomain)],
                      ['Client ID', zendeskOAuth.clientIdConfigured],
                      ['Client secret', zendeskOAuth.clientSecretConfigured],
                      ['Webhook secret', zendeskOAuth.webhookSecretConfigured],
                    ].map(([label, ok]) => (
                      <div key={label} style={{marginBottom: 4}}>
                        <Tag color={ok ? 'green' : 'red'}>{ok ? 'saved' : 'missing'}</Tag>
                        {label}
                      </div>
                    ))}
                    <p className="drop-text" style={{marginTop: 8}}>
                      Status: <strong>{zendeskOAuth.status}</strong>. Activate needs all four.
                    </p>
                  </div>
                )}

                <Button
                  type="default"
                  className="settings-panel-btn"
                  onClick={() => saveZendeskOAuth(zendeskOAuth?.status)}
                  loading={zendeskSaving}
                  disabled={!zendeskSubdomain}
                >
                  Save
                </Button>
                {' '}
                {zendeskOAuth?.status === 'active' ? (
                  <Button
                    type="default"
                    className="settings-panel-btn"
                    onClick={() => saveZendeskOAuth('disabled')}
                    loading={zendeskSaving}
                  >
                    Disable
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    className="settings-panel-btn"
                    onClick={() => saveZendeskOAuth('active')}
                    loading={zendeskSaving}
                    disabled={!zendeskSubdomain}
                  >
                    Activate
                  </Button>
                )}

                {zendeskOAuth?.webhookSecretConfigured && (
                  <>
                    {' '}
                    <Popconfirm
                      title="Generate a new webhook secret?"
                      description={
                        <span>
                          Current secret stops working immediately.<br />
                          Zendesk rejects every event until the new one is pasted in.
                        </span>
                      }
                      okText="Regenerate"
                      cancelText="Cancel"
                      onConfirm={() => saveZendeskOAuth(zendeskOAuth?.status, {regenerateWebhookSecret: true})}
                    >
                      <Button danger type="default" className="settings-panel-btn" loading={zendeskSaving}>
                        Regenerate webhook secret
                      </Button>
                    </Popconfirm>
                  </>
                )}

                {zendeskMessage && <p className="drop-text">{zendeskMessage}</p>}
              </div>
            </div>
            </>
          ) : null}

                </div>
              ),
            },
            {
              key: 'client-filter',
              label: 'Clients',
              children: (
                <div className="selectors settings-tab-panel">
          <div className="brand-workspace">
            <ActiveBrandBlock />
            <Divider className="brand-workspace__divider" />
          </div>
          <div className="client-filters-summary">
            <h2 className="title-2">Clients</h2>
            <p className="client-filters-summary__hint">
              Each client acts as a filter that groups one or more brands for your account. You can have zero or more clients.
            </p>
            <div className="client-filters-summary__toolbar">
              <Button
                type="text"
                size="small"
                className="client-filters-summary__expand-toggle-btn"
                disabled={clientsWithBrandLists.length === 0}
                onClick={toggleAllClientBrandDisclosures}
                aria-label={allBrandDisclosuresExpanded ? 'Hide all client brand lists' : 'Show all client brand lists'}
              >
                {allBrandDisclosuresExpanded ? 'Hide all brands' : 'Show all brands'}
              </Button>
            </div>
            <Table
              className="client-filters-summary__table"
              rowKey="id"
              dataSource={clients}
              pagination={false}
              bordered
              size="middle"
              locale={{ emptyText: 'No clients yet. Create one below.' }}
              columns={[
                {
                  title: 'Client name',
                  dataIndex: 'name',
                  key: 'name',
                  ellipsis: true,
                },
                {
                  title: 'Brands',
                  key: 'brands',
                  render: (_, record) => {
                    const ids = record.brandIds || [];
                    const count = ids.length;
                    const label = `${count} ${count === 1 ? 'Brand' : 'Brands'}`;
                    if (count === 0) {
                      return (
                        <span className="client-filters-summary__brands-count-only">{label}</span>
                      );
                    }
                    const rowOpen = brandsDisclosureOpenByClientId[record.id] ?? false;
                    return (
                      <details
                        className="client-filters-summary__brands-disclosure"
                        open={rowOpen}
                        onToggle={(e) => {
                          // Read open synchronously — do not read e.currentTarget inside the setState
                          // updater; it runs later and the event may be pooled/cleared (currentTarget null).
                          const node = e.currentTarget ?? e.target?.closest?.('details');
                          if (!node) return;
                          const isOpen = node.open;
                          setBrandsDisclosureOpenByClientId((prev) => ({
                            ...prev,
                            [record.id]: isOpen,
                          }));
                        }}
                      >
                        <summary className="client-filters-summary__brands-summary">
                          <span className="client-filters-summary__brands-caret" aria-hidden>
                            {rowOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                          </span>
                          {label}
                        </summary>
                        <ul className="client-filters-summary__brand-list">
                          {ids.map((id) => {
                            const b = (brands || []).find((x) => x.id === id);
                            return (
                              <li key={id}>{b?.name ?? id}</li>
                            );
                          })}
                        </ul>
                      </details>
                    );
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 200,
                  render: (_, record) => (
                    <Space size="small" wrap>
                      <Button type="default" size="small" onClick={() => openEditClientModal(record.id)}>
                        Edit
                      </Button>
                      <Button type="default" danger size="small" onClick={() => handleDeleteClient(record.id)}>
                        Delete
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
            <Button
              type="default"
              className="client-filters-summary__add-btn"
              onClick={() => {
                setModalEditingClientId(null);
                setClientModalBrandSearch('');
                addClientForm.resetFields();
                setAddClientModalOpen(true);
              }}
            >
              Add new client
            </Button>

            <Modal
              title={modalEditingClientId ? 'Edit client' : 'Add new client'}
              open={addClientModalOpen}
              onCancel={closeClientModal}
              onOk={handleClientModalSubmit}
              okText={modalEditingClientId ? 'Save' : 'Create'}
              destroyOnClose
              width={880}
              className="add-client-modal"
              bodyStyle={{ paddingTop: 16 }}
            >
              <Form
                form={addClientForm}
                layout="vertical"
                className="add-client-modal-form"
                initialValues={{ brandIds: [] }}
              >
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Enter a name' }]}
                >
                  <Input placeholder="Client name" autoComplete="off" />
                </Form.Item>
                <Form.Item label="Brands">
                  <div className="client-modal-brands-field">
                    <div className="client-modal-brands-toolbar">
                      <Button
                        type="link"
                        htmlType="button"
                        size="small"
                        disabled={!(brands && brands.length)}
                        onClick={() => addClientForm.setFieldValue('brandIds', ['all'])}
                      >
                        Select all brands
                      </Button>
                      <Button
                        type="link"
                        htmlType="button"
                        size="small"
                        onClick={() => addClientForm.setFieldValue('brandIds', [])}
                      >
                        Clear
                      </Button>
                    </div>
                    <p className="client-modal-brands-hint">
                      Search below, then tick brands. Use &quot;Select all brands&quot; to include every brand.
                    </p>
                    <Form.Item name="brandIds" noStyle>
                      <ClientModalBrandTable
                        brands={brands}
                        search={clientModalBrandSearch}
                        onSearchChange={setClientModalBrandSearch}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>
              </Form>
            </Modal>
          </div>
                </div>
              ),
            },
            {
              key: 'misc',
              label: 'Misc',
              children: (
                <div className="selectors settings-tab-panel">
          <div className="brand-workspace">
            <ActiveBrandBlock />
            <Divider className="brand-workspace__divider" />
          </div>

          <h2 className="title-2">Subscription</h2>
          <p className='margin'>You are billed based on number of emails received each month where the AI is enabled. You do not incur additional fees by turning on automations.</p>
          <a href='https://billing.stripe.com/p/login/3csaIj88Dcrn8j63cc' target='_blank'>Manage subscription</a>

          <h2 className="title-2">TEST MODE</h2>
          <p>Will send the AI's logic in internal notes to Gorgias.</p>
          <br></br>
          <Switch
            style={lockSettings}
            checkedChildren=""
            unCheckedChildren=""
            checked={testMode}
            onChange={handleToggleTestMode}
          />

          <br></br>
          <br></br>
          <br /><br />
          <br /><br />
                </div>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title={`${historyDrawer.label} — Edit History`}
        open={historyDrawer.open}
        onClose={() => setHistoryDrawer({ open: false, field: null, label: '' })}
        width="min(1200px, 94vw)"
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : historyData.length === 0 ? (
          <p>No edit history found.</p>
        ) : (
          <Collapse accordion>
            {historyData.map((entry, i) => {
              const date = entry.editedAt?._seconds
                ? new Date(entry.editedAt._seconds * 1000)
                : null;
              return (
                <Collapse.Panel
                  key={entry.id || i}
                  header={
                    <span>
                      <strong>{entry.editedBy}</strong>
                      {date && <span style={{ marginLeft: 8, color: '#888' }}>
                        {date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')} {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                      </span>}
                    </span>
                  }
                >
                  <p style={{ marginBottom: 8 }}><strong>Changes:</strong></p>
                  <FieldHistoryDiff oldValue={entry.oldValue} newValue={entry.newValue} />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        )}
      </Drawer>
    </>
  )
}



function generateInput(key, placeholder, value, setValue) {
  return (
    <>
      <p className='key-title'>{key}</p>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="off"
      /><br /><br />
    </>
  );
}
